import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import {
  createDraftQuotePdfPreviewApi,
  createRecentQuotePdfPreviewApi,
  downloadDraftQuotePdfApi,
  downloadRecentQuotePdfApi,
  openCheckoutServiceForDraftedQuoteApi,
  publishDraftQuoteByIdApi
} from 'api';
import { setSelectedDraftedQuote } from 'redux/actions/quote';
import QuoteNotesDialog from 'pages/quotes/QuoteSummary/QuoteNotesDialog';
import { extractAttributesValues } from 'pages/quotes/utils/extractAttributesValues';
import { useStyles } from '../../styles';


const PatientActivitySummaryCard = ({ selectedTab }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    selectedDraftedQuote,
    draftQuotePdfPreview,
    loadingDraftQuotePdfPreview,
    recentQuotePdfPreview,
    loadingRecentQuotePdfPreview,
    loadingDraftedQuotes,
    loadingDraftQuotePdfDownload,
    loadingRecentQuotePdfDownload,
    customerAmastyQuotes,
    allQuoteProducts
  } = useSelector((state: IReduxState) => state.quote);

  const [notesDialog, setNotesDialog] = useState({ isOpen: false, dialogType: '' });
  const [openPreviewQuote, setOpenPreviewQuote] = useState(false);
  const [quoteData, setQuoteData] = useState({
    quoteNotes: '',
    expirationDate: null,
    quoteName: '',
    quoteDiscount: { price_type: '', price: null }
  });

  const handleProceedToPayNow = async ({ quoteDiscount }) => {
    if (selectedDraftedQuote?._id) {
      openCheckoutServiceForDraftedQuoteApi({ dispatch, id: selectedDraftedQuote._id, quoteDiscount });
    }
  };

  const handleCreateQuote = async ({ quoteNotes, expirationDate, startDate = null, mustUsedDate = null, quoteName, quoteDiscount }) => {
    if (selectedDraftedQuote?._id) {
      publishDraftQuoteByIdApi({ dispatch, id: selectedDraftedQuote._id, quoteNotes, expirationDate, startDate, mustUsedDate, quoteName, quoteDiscount });
    }

    setNotesDialog({isOpen: false, dialogType: ''});
    setQuoteData({quoteNotes: '', expirationDate: null, quoteName: '', quoteDiscount: { price_type: '', price: null }});
  };

  const extractProductData = (quoteData, allQuoteProducts, attributesList) => {
    return quoteData.items.map((item) => {
      const selectedProduct = allQuoteProducts.find((product) => product.sku === item.sku);
      const customAttributes = extractAttributesValues(selectedProduct.custom_attributes, attributesList);

      return {
        ...selectedProduct,
        ...customAttributes,
        name: selectedProduct.name,
        price: selectedProduct.price,
      };
    });
  };

  const extractItemsData = (quoteData, attributesList) => {
    return quoteData.items.map((item) => {
      const customAttributes = extractAttributesValues(item.custom_attributes, attributesList);

      return {
        product_option: {
          extension_attributes: {
            quotepro_discounts: {
              price_type: item.discount_price_type || '',
              price: item.discount_price || null
            },
            subscription_id: customAttributes.subscription_id || null,
            custom_options: []
          }
        },
        _id: item._id,
        sku: item.sku,
        qty: item.qty,
        price: item.price,
      };
    });
  };

  const processQuoteData = (quoteData, allQuoteProducts, attributesList) => {
    const products = extractProductData(quoteData, allQuoteProducts, attributesList);
    const items = extractItemsData(quoteData, attributesList);
    return { ...quoteData, products, items };
  };

  const handleDownloadPdf = async () => {
    if (selectedTab === 'draft_patient_history') {
      const { quoteNotes, expirationDate, quoteName, quoteDiscount } = quoteData;
      downloadDraftQuotePdfApi({ dispatch, id: selectedDraftedQuote._id, quoteNotes, expirationDate, quoteName, quoteDiscount });
    } else {
      const quoteData = customerAmastyQuotes.find((quote) => quote?.increment_id === selectedDraftedQuote._id);
      const attributesList = [
        'image', 'subscription_options', 'subscription_active', 'catalog_product_unit', 'savings',
        'subscription_label', 'additional_subscription_price', 'quotepro_discounts', 'short_description'
      ];
      const updatedQuoteData = processQuoteData(quoteData, allQuoteProducts, attributesList);
      downloadRecentQuotePdfApi({ dispatch, id: selectedDraftedQuote._id, quoteData: updatedQuoteData });
    }
  };

  const handlePreviewQuote = (quoteData) => {
    if (selectedTab === 'draft_patient_history') {
      setQuoteData(quoteData);
      const { quoteNotes, expirationDate, startDate = null, mustUsedDate = null, quoteName, quoteDiscount } = quoteData;
      setNotesDialog({ isOpen: false, dialogType: '' });
      setOpenPreviewQuote(true);
      createDraftQuotePdfPreviewApi({ dispatch, id: selectedDraftedQuote._id, quoteNotes, expirationDate, startDate, mustUsedDate, quoteName, quoteDiscount });
    } else {
      const quoteData = customerAmastyQuotes.find((quote) => quote?.increment_id === selectedDraftedQuote._id);
      const attributesList = [
        'image', 'subscription_options', 'subscription_active', 'catalog_product_unit', 'savings',
        'subscription_label', 'additional_subscription_price', 'quotepro_discounts', 'short_description'
      ];
      const updatedQuoteData = processQuoteData(quoteData, allQuoteProducts, attributesList);
      setOpenPreviewQuote(true);
      createRecentQuotePdfPreviewApi({ dispatch, id: selectedDraftedQuote._id, quoteData: updatedQuoteData });
    }
  };


  const handleConfirmNotesDialog = (quoteData) => {
    if (notesDialog.dialogType === 'createQuote') {
      handleCreateQuote(quoteData);
    } else if (notesDialog.dialogType === 'previewQuote') {
      handlePreviewQuote(quoteData);
    } else if (notesDialog.dialogType === 'payNow') {
      handleProceedToPayNow(quoteData);
    }
  };

  return (
    <Card className={`${classes.bgBlue} root-card`}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={2}
        border={1}
        borderRadius={4}
        borderColor="grey.300"
      >
        {selectedTab === 'draft_patient_history' &&
          <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
            <Button
              className={classes.customQuoteSummaryButton}
              variant="contained"
              color="primary"
              size="large"
              disabled={!selectedDraftedQuote._id}
              onClick={() => setNotesDialog({ isOpen: true, dialogType: 'createQuote' })}
              fullWidth
            >
              Create a Quote
            </Button>
          </Box>
        }

        {selectedTab === 'draft_patient_history' &&
          <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
            <Button
              fullWidth
              className={classes.customQuoteSummaryButton}
              variant="contained"
              color="primary"
              size="large"
              disabled={!selectedDraftedQuote._id}
              onClick={() => setNotesDialog({ isOpen: true, dialogType: 'payNow' })}
            >
              Pay Now
            </Button>
          </Box>
        }

        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={!selectedDraftedQuote._id}
            fullWidth
            onClick={() => selectedTab === 'draft_patient_history' ? setNotesDialog({ isOpen: true, dialogType: 'previewQuote' }) : handlePreviewQuote(quoteData)}
          >
            Preview Quote
          </Button>
        </Box>
      </Box>


      {notesDialog.isOpen && selectedTab === 'draft_patient_history' &&
        <QuoteNotesDialog
          isOpen={notesDialog.isOpen}
          onClose={() => setNotesDialog({isOpen: false, dialogType: ''})}
          onConfirm={handleConfirmNotesDialog}
          dialogType={notesDialog.dialogType}
          total={selectedDraftedQuote.amount}
        />
      }

      <Dialog
        open={openPreviewQuote}
        onClose={() => {
          dispatch(setSelectedDraftedQuote({}));
          setOpenPreviewQuote(false);
        }}
        maxWidth={'md'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" className="mt-4">Preview Quote</Typography>
        </DialogTitle>
        <DialogContent  className="m-4">
          {loadingRecentQuotePdfPreview && selectedTab !== 'draft_patient_history' ? <LinearProgress /> : <div dangerouslySetInnerHTML={{ __html: recentQuotePdfPreview || ''}} />}
          {loadingDraftQuotePdfPreview && selectedTab === 'draft_patient_history' ? <LinearProgress /> : <div dangerouslySetInnerHTML={{ __html: draftQuotePdfPreview || ''}} />}
        </DialogContent>
        <DialogActions>
          {selectedTab === 'draft_patient_history' &&
            <Button
              className="max-w-190"
              size="small"
              variant="contained"
              color="primary"
              disabled={!selectedDraftedQuote._id}
              fullWidth
              onClick={() => {
                handleCreateQuote(quoteData);
                setOpenPreviewQuote(false);
              }}
            >
              <Icon fontSize="small">send</Icon>
              <span className="ml-2">CREATE A QUOTE</span>
            </Button>
          }
          <Button
            className="max-w-190 mr-4"
            size="small"
            variant="contained"
            color="primary"
            disabled={!selectedDraftedQuote._id}
            fullWidth
            onClick={handleDownloadPdf}
          >
            <Icon fontSize="small">download</Icon>
            <span className="ml-2">DOWNLOAD</span>
          </Button>
        </DialogActions>
        <BackdropCircular loading={loadingDraftQuotePdfDownload || loadingDraftedQuotes || loadingRecentQuotePdfDownload} />
      </Dialog>
    </Card>
  );
};

export default PatientActivitySummaryCard;
